import { ChangeEvent, DragEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Languages, Loader2, Plus, Sparkles, Star, Upload, X, ZoomIn } from 'lucide-react';
import { AiPhotoTransformModal } from './AiPhotoTransformModal';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ContentLocale, Product, ProductCategory, ProductType, ProductVariant } from '../types';
import { ProductInput, resolveImageUrl, translateProduct, uploadImage } from '../lib/api';
import { useToast } from '../ui/UIContext';
import { ImageLightbox } from './ImageLightbox';

const CATEGORY_OPTIONS: ProductCategory[] = ['CAFTAN', 'TAKCHITA', 'DJELLABA', 'OTHER'];
const TYPE_OPTIONS: ProductType[] = ['CUSTOM', 'READY_TO_WEAR'];
const SIZE_PRESETS: string[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const CONTENT_LOCALES: ContentLocale[] = ['ar', 'fr', 'en'];

// 10 MB matches the server-side cap in LocalDiskImageStorage.MAX_BYTES.
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
// Keep the gallery to a reasonable size — UX gets noisy beyond ~8 thumbs.
const MAX_GALLERY_IMAGES = 8;

interface Props {
  product: Product | null;
  onClose: () => void;
  onSubmit: (input: ProductInput) => Promise<void>;
}

const EMPTY: ProductInput = {
  title: '',
  price: 0,
  description: '',
  sourceLocale: 'ar',
  translations: {},
  imageUrl: '',
  images: [],
  rating: 4.5,
  measurementDuration: '60 min',
  category: 'CAFTAN',
  productType: 'CUSTOM',
  variants: [],
};

export function ProductFormModal({ product, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ProductInput>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  // Snapshot of form.imageUrl at the moment the AI modal opened. Used
  // when the modal finishes to decide whether to promote the first kept
  // pose to the primary slot (only if the form's primary is still the
  // original source — i.e. the admin didn't manually swap mid-flight).
  const [aiSourceUrl, setAiSourceUrl] = useState<string>('');
  const [translating, setTranslating] = useState(false);
  // Kept separate from `error` (which renders at the very bottom of a long
  // scrollable modal): a translation failure has to be visible next to the
  // button that triggered it, otherwise the click looks like a no-op.
  const [translateError, setTranslateError] = useState<string | null>(null);
  // Image shown full-size, or null. The form only ever displays thumbnails,
  // which are too small to write a garment description from.
  const [zoomed, setZoomed] = useState<{ url: string; alt: string } | null>(null);
  const showToast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Revoke any leftover blob URL when the modal unmounts or a new file is picked
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  async function uploadFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setError(t('adminProducts.image.errors.notImage'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(t('adminProducts.image.errors.tooLarge'));
      return;
    }
    setError(null);

    // Local preview while the upload is in flight — instant visual feedback
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setUploading(true);
    try {
      const uploaded = await uploadImage(file);
      setForm((prev) => ({ ...prev, imageUrl: uploaded.url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void uploadFile(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) void uploadFile(file);
  }

  async function uploadGalleryFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setError(t('adminProducts.image.errors.notImage'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(t('adminProducts.image.errors.tooLarge'));
      return;
    }
    if ((form.images?.length ?? 0) >= MAX_GALLERY_IMAGES) {
      setError(t('adminProducts.gallery.errors.full', { max: MAX_GALLERY_IMAGES }));
      return;
    }
    setError(null);
    setGalleryUploading(true);
    try {
      const uploaded = await uploadImage(file);
      setForm((prev) => ({
        ...prev,
        images: [...(prev.images ?? []), uploaded.url],
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setGalleryUploading(false);
    }
  }

  function handleGalleryFileChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    // Reset input so picking the same file again still fires change
    event.target.value = '';
    // Upload sequentially so we never exceed MAX_GALLERY_IMAGES mid-batch
    void (async () => {
      for (const file of files) {
        if ((form.images?.length ?? 0) >= MAX_GALLERY_IMAGES) break;
        await uploadGalleryFile(file);
      }
    })();
  }

  function removeGalleryImage(index: number) {
    setForm((prev) => ({
      ...prev,
      images: (prev.images ?? []).filter((_, i) => i !== index),
    }));
  }

  /**
   * Promote a gallery image to primary. The current primary (if any) is
   * pushed into the gallery at the slot the chosen image came from, so
   * the admin never loses an image — it's always a swap.
   */
  function setAsPrimary(index: number) {
    setForm((prev) => {
      const list = prev.images ?? [];
      if (index < 0 || index >= list.length) return prev;
      const newPrimary = list[index];
      if (newPrimary === prev.imageUrl) return prev;
      const nextImages = [...list];
      if (prev.imageUrl && prev.imageUrl.trim() !== '') {
        // Swap: old primary goes where the picked image used to be
        nextImages[index] = prev.imageUrl;
      } else {
        // No primary yet — just remove the picked image from the gallery
        nextImages.splice(index, 1);
      }
      return { ...prev, imageUrl: newPrimary, images: nextImages };
    });
  }

  const dndSensors = useSensors(
    // 5 px drag distance before pointer counts as a drag — lets the remove
    // button still receive normal clicks without triggering a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleGalleryDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((prev) => {
      const list = prev.images ?? [];
      const from = list.indexOf(String(active.id));
      const to = list.indexOf(String(over.id));
      if (from < 0 || to < 0) return prev;
      return { ...prev, images: arrayMove(list, from, to) };
    });
  }

  useEffect(() => {
    if (product) {
      setForm({
        title: product.title,
        price: product.price,
        originalPrice: product.originalPrice,
        description: product.description,
        sourceLocale: product.sourceLocale ?? 'fr',
        translations: product.translations ?? {},
        imageUrl: product.imageUrl,
        sourceImageUrl: product.sourceImageUrl,
        images: product.images ?? [],
        rating: product.rating,
        measurementDuration: product.measurementDuration,
        category: product.category ?? 'OTHER',
        productType: product.productType ?? 'CUSTOM',
        variants: product.variants ?? [],
      });
    } else {
      setForm(EMPTY);
    }
  }, [product]);

  function updateVariant(index: number, patch: Partial<ProductVariant>) {
    setForm((prev) => {
      const list = [...(prev.variants ?? [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, variants: list };
    });
  }

  function removeVariant(index: number) {
    setForm((prev) => ({
      ...prev,
      variants: (prev.variants ?? []).filter((_, i) => i !== index),
    }));
  }

  function addVariant(size: string) {
    setForm((prev) => {
      const list = prev.variants ?? [];
      if (list.some((v) => v.size === size)) return prev;
      return { ...prev, variants: [...list, { size, stock: 1 }] };
    });
  }

  const sourceLocale = (form.sourceLocale ?? 'ar') as ContentLocale;
  const targetLocales = CONTENT_LOCALES.filter((l) => l !== sourceLocale);

  function updateTranslation(
    locale: string,
    patch: { title?: string; description?: string },
  ) {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...(prev.translations ?? {}),
        [locale]: { ...(prev.translations?.[locale] ?? {}), ...patch },
      },
    }));
  }

  /**
   * Machine-translate the source title + description into the target locales
   * via the backend (Gemini). Fills the editable translation fields — the
   * admin reviews/refines before saving. Never overwrites a target the admin
   * is mid-editing without their click.
   */
  async function handleTranslate() {
    // Deliberately NOT gated by `disabled`: a disabled button swallows the
    // click and looks broken. Let it fire and explain what is missing.
    if (!form.title.trim() || !form.description.trim()) {
      setTranslateError(t('adminProducts.i18n.errors.needSource'));
      return;
    }
    setTranslateError(null);
    setTranslating(true);
    try {
      const res = await translateProduct({
        sourceLocale,
        title: form.title,
        description: form.description,
      });
      const filled = Object.keys(res?.translations ?? {}).length;
      if (!filled) {
        // 2xx with an empty map — treat as a failure rather than silently
        // leaving every target field blank.
        setTranslateError(t('adminProducts.i18n.errors.empty'));
        return;
      }
      setForm((prev) => ({
        ...prev,
        translations: { ...(prev.translations ?? {}), ...res.translations },
      }));
      showToast(t('adminProducts.i18n.translated', { count: filled }), 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Translation failed';
      setTranslateError(msg);
      showToast(msg, 'error');
    } finally {
      setTranslating(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.imageUrl) {
      setError(t('adminProducts.image.errors.required'));
      return;
    }
    if (form.productType === 'READY_TO_WEAR') {
      const list = form.variants ?? [];
      if (list.length === 0) {
        setError(t('adminProducts.variants.errors.required'));
        return;
      }
      if (list.some((v) => !v.size.trim() || v.stock < 0)) {
        setError(t('adminProducts.variants.errors.invalidRow'));
        return;
      }
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {product ? t('adminProducts.form.editTitle') : t('adminProducts.form.newTitle')}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="product-source-locale" className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.i18n.sourceLocaleLabel')}
            </label>
            <select
              id="product-source-locale"
              value={sourceLocale}
              onChange={(e) => setForm({ ...form, sourceLocale: e.target.value })}
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {CONTENT_LOCALES.map((l) => (
                <option key={l} value={l}>
                  {t(`adminProducts.i18n.locales.${l}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('adminProducts.i18n.sourceLocaleHint')}
            </p>
          </div>

          <div>
            <label htmlFor="product-title" className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.fields.title')}
            </label>
            <input
              id="product-title"
              type="text"
              required
              dir="auto"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="product-productType" className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.fields.productType')}
            </label>
            <select
              id="product-productType"
              value={form.productType ?? 'CUSTOM'}
              onChange={(e) =>
                setForm({ ...form, productType: e.target.value as ProductType })
              }
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {TYPE_OPTIONS.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`catalog.type.values.${tp}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('adminProducts.fields.productTypeHint')}
            </p>
          </div>

          <div>
            <label htmlFor="product-category" className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.fields.category')}
            </label>
            <select
              id="product-category"
              value={form.category ?? 'OTHER'}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value as ProductCategory })
              }
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {t(`catalog.category.values.${cat}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('adminProducts.fields.price')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('adminProducts.fields.originalPrice')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.originalPrice ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    originalPrice: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.fields.description')}
            </label>
            <textarea
              id="product-description"
              required
              rows={3}
              dir="auto"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Translations — machine-translated from the source copy, editable.
              The storefront shows the visitor's-language translation if present,
              else falls back to the source title/description above. */}
          <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="block text-sm font-medium text-gray-700">
                  {t('adminProducts.i18n.translationsLabel')}
                </span>
                <span className="block text-xs text-gray-500">
                  {t('adminProducts.i18n.translationsHint')}
                </span>
              </div>
              <button
                type="button"
                onClick={handleTranslate}
                disabled={translating}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {translating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Languages className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                {translating
                  ? t('adminProducts.i18n.translating')
                  : t('adminProducts.i18n.translateButton')}
              </button>
            </div>

            {translateError && (
              <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {translateError}
              </div>
            )}

            {targetLocales.map((loc) => (
              <div key={loc} className="space-y-1.5">
                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t(`adminProducts.i18n.locales.${loc}`)}
                </span>
                <input
                  type="text"
                  dir="auto"
                  placeholder={t('adminProducts.fields.title')}
                  aria-label={`${t(`adminProducts.i18n.locales.${loc}`)} — ${t('adminProducts.fields.title')}`}
                  value={form.translations?.[loc]?.title ?? ''}
                  onChange={(e) => updateTranslation(loc, { title: e.target.value })}
                  className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <textarea
                  rows={2}
                  dir="auto"
                  placeholder={t('adminProducts.fields.description')}
                  aria-label={`${t(`adminProducts.i18n.locales.${loc}`)} — ${t('adminProducts.fields.description')}`}
                  value={form.translations?.[loc]?.description ?? ''}
                  onChange={(e) => updateTranslation(loc, { description: e.target.value })}
                  className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.fields.image')}{' '}
              <span className="text-xs text-amber-600 font-normal">
                ★ {t('adminProducts.image.primaryHint')}
              </span>
            </label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex items-center gap-4 rounded-md border-2 border-dashed p-3 cursor-pointer transition ${
                dragOver
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {uploading ? (
                <div className="w-20 h-20 rounded bg-gray-100 flex items-center justify-center text-indigo-500">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : localPreview || form.imageUrl ? (
                // The surrounding drop zone opens the file picker on click, so
                // zooming gets its own control rather than hijacking that.
                <div className="relative w-20 h-20 shrink-0">
                  <img
                    src={localPreview ?? resolveImageUrl(form.imageUrl)}
                    alt=""
                    className="w-20 h-20 rounded object-cover bg-gray-100"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoomed({
                        url: localPreview ?? form.imageUrl,
                        alt: t('adminProducts.fields.image'),
                      });
                    }}
                    aria-label={t('adminProducts.image.zoom')}
                    title={t('adminProducts.image.zoom')}
                    className="absolute bottom-0.5 end-0.5 rounded-full bg-white/90 hover:bg-white p-1 shadow text-gray-700"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 rounded bg-gray-100 flex items-center justify-center text-gray-400">
                  <Upload className="w-6 h-6" />
                </div>
              )}
              <div className="text-sm text-gray-600">
                <div className="font-medium text-gray-800">
                  {t('adminProducts.image.dropOrClick')}
                </div>
                <div className="text-xs text-gray-500">{t('adminProducts.image.hint')}</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            <input
              type="text"
              inputMode="url"
              placeholder={t('adminProducts.image.urlPlaceholder')}
              value={form.imageUrl}
              onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
              className="mt-2 w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {/* AI photo enhance — Gemini 2.5 Flash Image, 4-pose batch.
                Disabled until the admin has uploaded a primary image. */}
            <button
              type="button"
              onClick={() => {
                setAiSourceUrl(form.imageUrl);
                setAiModalOpen(true);
              }}
              disabled={!form.imageUrl || uploading}
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              {t('adminProducts.ai.button')}
            </button>
            <p className="mt-1 text-xs text-gray-500">{t('adminProducts.ai.buttonHint')}</p>

            {/* Source photo — the raw shot the AI imagery came from. Admin
                working asset: the API hides it from the storefront. Shown
                here so a later re-run has its input, since accepting a pose
                overwrites the primary image. */}
            {form.sourceImageUrl && (
              <div className="mt-3 flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50/60 p-2">
                {/* Clickable: a 56px thumbnail is not enough to write a
                    garment description from, which is exactly what this photo
                    is kept for. */}
                <button
                  type="button"
                  onClick={() => setZoomed({
                    url: form.sourceImageUrl as string,
                    alt: t('adminProducts.image.sourceLabel'),
                  })}
                  title={t('adminProducts.image.zoom')}
                  className="shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <img
                    src={resolveImageUrl(form.sourceImageUrl)}
                    alt={t('adminProducts.image.sourceLabel')}
                    className="w-14 h-14 rounded object-cover bg-gray-100 cursor-zoom-in hover:opacity-90 transition"
                  />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-gray-700">
                    {t('adminProducts.image.sourceLabel')}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {t('adminProducts.image.sourceHint')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAiSourceUrl(form.sourceImageUrl as string);
                    setAiModalOpen(true);
                  }}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-xs rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5 disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('adminProducts.image.regenerateFromSource')}
                </button>
              </div>
            )}
          </div>

          {/* Gallery — secondary images shown on the product detail page */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminProducts.gallery.label')}{' '}
              <span className="text-xs text-gray-500">
                ({(form.images?.length ?? 0)}/{MAX_GALLERY_IMAGES})
              </span>
            </label>
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleGalleryDragEnd}
            >
              <SortableContext items={form.images ?? []} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-4 gap-3">
                  {(form.images ?? []).map((url, idx) => (
                    <SortableThumb
                      key={url}
                      id={url}
                      url={url}
                      index={idx}
                      onRemove={() => removeGalleryImage(idx)}
                      onSetPrimary={() => setAsPrimary(idx)}
                      removeLabel={t('adminProducts.gallery.remove')}
                      setPrimaryLabel={t('adminProducts.gallery.setAsPrimary')}
                      altText={t('adminProducts.gallery.imageAlt', { n: idx + 1 })}
                      dragLabel={t('adminProducts.gallery.reorder')}
                      zoomLabel={t('adminProducts.image.zoom')}
                      onZoom={() =>
                        setZoomed({
                          url,
                          alt: t('adminProducts.gallery.imageAlt', { n: idx + 1 }),
                        })
                      }
                    />
                  ))}

                  {(form.images?.length ?? 0) < MAX_GALLERY_IMAGES && (
                    <button
                      type="button"
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={galleryUploading}
                      aria-label={t('adminProducts.gallery.add')}
                      className="aspect-square rounded-md border-2 border-dashed border-gray-300 hover:border-indigo-500 hover:bg-indigo-50 flex flex-col items-center justify-center text-gray-400 hover:text-indigo-500 transition disabled:opacity-50"
                    >
                      {galleryUploading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-6 h-6" />
                          <span className="text-xs mt-1">{t('adminProducts.gallery.add')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </SortableContext>
            </DndContext>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryFileChange}
              className="hidden"
            />
            <p className="mt-1 text-xs text-gray-500">{t('adminProducts.gallery.hint')}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('adminProducts.fields.rating')}
              </label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                required
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('adminProducts.fields.measurementDuration')}
              </label>
              <input
                type="text"
                required
                value={form.measurementDuration}
                onChange={(e) => setForm({ ...form, measurementDuration: e.target.value })}
                className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {form.productType === 'READY_TO_WEAR' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('adminProducts.variants.label')}
              </label>
              <div className="space-y-2">
                {(form.variants ?? []).map((v, idx) => (
                  <div key={`${v.size}-${idx}`} className="flex items-center gap-2">
                    <input
                      type="text"
                      aria-label={t('adminProducts.variants.sizeLabel')}
                      value={v.size}
                      onChange={(e) => updateVariant(idx, { size: e.target.value.toUpperCase() })}
                      className="w-24 rounded-md border border-gray-300 text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <input
                      type="number"
                      min="0"
                      aria-label={t('adminProducts.variants.stockLabel')}
                      value={v.stock}
                      onChange={(e) =>
                        updateVariant(idx, { stock: Math.max(0, parseInt(e.target.value, 10) || 0) })
                      }
                      className="w-28 rounded-md border border-gray-300 text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="text-xs text-gray-500">
                      {t('adminProducts.variants.stockSuffix')}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeVariant(idx)}
                      aria-label={t('adminProducts.variants.remove')}
                      className="ms-auto text-gray-400 hover:text-red-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {SIZE_PRESETS.filter(
                  (s) => !(form.variants ?? []).some((v) => v.size === s),
                ).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addVariant(s)}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Plus className="w-3 h-3" />
                    {s}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {t('adminProducts.variants.hint')}
              </p>
            </div>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {t('adminProducts.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || uploading || galleryUploading}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? t('adminProducts.form.saving') : t('adminProducts.form.save')}
            </button>
          </div>
        </form>
      </div>

      {/* AI photo-transform modal (Gemini 2.5 Flash Image — 4-pose set).
          Mounts on demand so its polling timer doesn't run unless the
          admin asked for it. Accept = the kept transformed URLs are
          appended to the product image gallery (first one becomes the
          primary imageUrl if the slot is still empty). The admin can
          then save as usual. */}
      {aiModalOpen && (
        <AiPhotoTransformModal
          // aiSourceUrl, not form.imageUrl: a re-run starts from the stored
          // source photo, since form.imageUrl is by then a generated image
          // and transforming a generation compounds its artefacts.
          originalImageUrl={aiSourceUrl || form.imageUrl}
          onClose={() => setAiModalOpen(false)}
          onAccept={(transformedUrls) => {
            if (transformedUrls.length === 0) {
              setAiModalOpen(false);
              return;
            }
            setForm((prev) => {
              const existingGallery = prev.images ?? [];
              // Promote the first kept pose to the primary slot if the
              // form's primary is still the same URL we sent in (i.e.
              // the admin didn't manually replace the source mid-run).
              // Otherwise keep the existing primary and append all kept
              // poses to the gallery.
              const [firstKept, ...rest] = transformedUrls;
              const shouldReplacePrimary =
                !prev.imageUrl || prev.imageUrl === aiSourceUrl;
              const newPrimary = shouldReplacePrimary ? firstKept : prev.imageUrl;
              const gallery = shouldReplacePrimary
                ? [...existingGallery, ...rest]
                : [...existingGallery, ...transformedUrls];
              // Cap the gallery at MAX_GALLERY_IMAGES — drop overflow.
              const cappedGallery = gallery.slice(0, MAX_GALLERY_IMAGES);
              // Record the photo the poses were generated from, at the one
              // moment it is still known: accepting a pose overwrites the
              // primary image, and nothing else on the product remembers it.
              // A source already on the product wins — it is the true
              // original, while aiSourceUrl may be a generated image the
              // admin re-ran the transform on.
              const sourceImageUrl = prev.sourceImageUrl || aiSourceUrl || prev.imageUrl;
              return { ...prev, imageUrl: newPrimary, images: cappedGallery, sourceImageUrl };
            });
            setLocalPreview((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
            setAiModalOpen(false);
          }}
        />
      )}

      {zoomed && (
        <ImageLightbox
          url={zoomed.url}
          alt={zoomed.alt}
          onClose={() => setZoomed(null)}
        />
      )}
    </div>
  );
}

interface SortableThumbProps {
  id: string;
  url: string;
  index: number;
  onRemove: () => void;
  onSetPrimary: () => void;
  removeLabel: string;
  setPrimaryLabel: string;
  altText: string;
  dragLabel: string;
  onZoom: () => void;
  zoomLabel: string;
}

/**
 * One gallery thumbnail. Wrapped in dnd-kit's {@code useSortable} so the
 * whole thumb is draggable, but the drag listeners are attached to a small
 * grip handle (top-left) — that keeps the other action buttons (set-primary
 * top-center, remove top-right) and the underlying image free of drag
 * activation.
 */
function SortableThumb({
  id,
  url,
  index,
  onRemove,
  onSetPrimary,
  removeLabel,
  setPrimaryLabel,
  altText,
  dragLabel,
  onZoom,
  zoomLabel,
}: SortableThumbProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 'auto',
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square rounded-md overflow-hidden bg-gray-100 group"
    >
      <img
        src={resolveImageUrl(url)}
        alt={altText}
        className="w-full h-full object-cover pointer-events-none select-none"
        draggable={false}
      />

      {/* Drag grip — top-left. Cursor changes to grab/grabbing automatically. */}
      <button
        type="button"
        aria-label={dragLabel}
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 bg-white/90 hover:bg-white rounded-full p-1 shadow text-gray-600 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition touch-none"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>

      {/* Promote to primary — top-center. */}
      <button
        type="button"
        onClick={onSetPrimary}
        aria-label={setPrimaryLabel}
        title={setPrimaryLabel}
        className="absolute top-1 left-1/2 -translate-x-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow text-gray-700 hover:text-amber-500 opacity-0 group-hover:opacity-100 transition"
      >
        <Star className="w-3.5 h-3.5" />
      </button>

      {/* Remove — top-right. */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow text-gray-700 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Zoom — bottom-right. A button rather than a click on the tile: the
          tile is draggable, and a plain click handler there competes with the
          drag gesture. */}
      <button
        type="button"
        onClick={onZoom}
        aria-label={zoomLabel}
        title={zoomLabel}
        className="absolute bottom-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow text-gray-700 opacity-0 group-hover:opacity-100 transition"
      >
        <ZoomIn className="w-3.5 h-3.5" />
      </button>

      {/* Position badge — bottom-left. Helps confirm the new order visually. */}
      <span className="absolute bottom-1 left-1 bg-black/55 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
        {index + 1}
      </span>
    </div>
  );
}
